import {
  Download,
  FileText,
  Folder,
  FolderLock,
  FolderOpen,
  FolderPlus,
  RotateCcw,
  Share2,
  Star,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { useListSurface } from '../../../../components/table/listSurface';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { FolderModal } from '../components/FolderModal';
import { ShareModal } from '../components/ShareModal';
import {
  FILES_ONLY_SCOPES,
  RepositoryNavRail,
  type RepositoryScope,
} from '../components/RepositoryNavRail';
import { useRepositoryUpload } from '../upload/RepositoryUploadContext';
import { repositoryApi } from '../repositoryApi';
import {
  hasPermission,
  RepositoryPermissions,
  RepositoryItemType,
  type RepositoryFolderContext,
  type RepositoryItemListItem,
} from '../repositoryTypes';

/**
 * The repository — the controlled-document registry, and the container every file in `rsys_`
 * is filed into.
 *
 * **A folder listing, not a chart.** A repository is a hierarchy, and it deliberately does not
 * go through `<TreeChart>`: that deviation was written for a chart of *ranks*, where a
 * record's position is its most important field and a `Parent` column puts the parent a
 * screen away. Here the reader navigates INTO a folder and then wants the ordinary list
 * contract — server-side search, sort, paging, facets, the settings cog. **The breadcrumb
 * carries the position**, which is exactly what the tree rule says a table cannot do.
 *
 * The folder lives in the URL (`?folder=<id>`), so a folder is linkable, the back button
 * works, and a reload lands where the reader was.
 */
export function RepositoryIndexPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get('folder');

  /*
    The browse response carries the page AND the context the page sits in — one call, because
    they are one answer to "what am I looking at". `<IndexPage>` only wants the page, so the
    context is caught here on the way past.
  */
  const [context, setContext] = useState<RepositoryFolderContext | null>(null);

  const [sharing, setSharing] = useState<RepositoryItemListItem | null>(null);
  const [editing, setEditing] = useState<RepositoryItemListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    item: RepositoryItemListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /** Whether a file drag is currently over the canvas. Drives the drop-zone ring. */
  const [dragging, setDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueue } = useRepositoryUpload();

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /**
   * The names already in this folder, so a queued row can WARN about a collision.
   *
   * A warning rather than a refusal: the server suffixes — `scan (2).pdf` — because nobody
   * typed these names. Telling the reader beforehand is what makes the suffix expected
   * instead of a surprise.
   */
  const [siblingNames, setSiblingNames] = useState<string[]>([]);

  const queueFiles = useCallback(
    (files: FileList | File[] | null) => {
      if (!files || !folderId) return;

      enqueue(files, {
        folderId,
        folderName: context?.folder?.name ?? 'this folder',
        isControlled: context?.isControlled ?? false,
        existingNames: siblingNames,
      });
    },
    [enqueue, folderId, context, siblingNames],
  );

  /*
    The dock is mounted at app level and outlives this page, so a finished upload announces
    itself on the window rather than through a callback. That is the only thing that reaches
    a listing the queue knows nothing about.
  */
  useEffect(() => {
    function onUploaded() {
      setReloadKey((k) => k + 1);
    }

    window.addEventListener('repository:uploaded', onUploaded);

    return () => window.removeEventListener('repository:uploaded', onUploaded);
  }, []);

  /**
   * Which listing is showing — the rail's selection, and the one piece of state behind it.
   *
   * A UNION rather than a flag per view: the scopes are mutually exclusive, and four booleans
   * would make every impossible combination expressible and leave this page deciding which of
   * them wins.
   *
   * `folder` is the resting state and is what the breadcrumb navigates within; every other
   * value is tree-wide.
   */
  const [scope, setScope] = useState<RepositoryScope>('folder');

  /** This reader's pinned items, for the rail's Pinned section. */
  const [pinnedItems, setPinnedItems] = useState<
    { id: string; name: string; isFolder: boolean }[]
  >([]);

  const openFolder = useCallback(
    (id: string | null) => {
      // Replace rather than merge: a folder change invalidates the page number and any
      // search scoped to the folder being left.
      setSearchParams(id ? { folder: id } : {});
      setContext(null);

      /*
        Opening a folder LEAVES whatever view scope was on. A reader who is looking at what is
        expired and then clicks a folder has asked for that folder — leaving the scope set would
        show them the expired documents inside it, under a breadcrumb that says otherwise.
      */
      setScope('folder');
    },
    [setSearchParams],
  );

  /*
    The rail's Pinned section. One call, and only what the rail draws — the id, the name, and
    whether it is a folder.

    It reads the same `pinnedOnly` listing the scope shows rather than a second endpoint: there
    is one way to ask what this reader has pinned, and a rail fed by its own call is a second
    answer that can disagree with the list beside it. Capped, because a rail is a column of
    shortcuts rather than a listing — past a dozen the reader should be in the Pinned view.
  */
  useEffect(() => {
    const controller = new AbortController();

    repositoryApi
      .browse({ pinnedOnly: true, pageSize: 12, status: 'active' }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;

        setPinnedItems(
          result.page.items.map((item) => ({
            id: item.id,
            name: item.name,
            isFolder: item.itemType === RepositoryItemType.Folder,
          })),
        );
      })
      // Silent: the rail is chrome, and a Pinned section that would not load leaves it shorter
      // rather than taking the repository down with it.
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchItems = useCallback(
    async (query: IndexQueryState, signal: AbortSignal) => {
      const result = await repositoryApi.browse(
        {
          folderId,
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          /*
            TRASH is the deleted listing, and it overrules the status facet rather than sitting
            beside it: the rail and the facet are two controls over one axis, and the rail is
            the one the reader just used. Every other scope leaves the facet alone, so Show
            deleted in the cog still works exactly as it did.
          */
          status: scope === 'trash' ? 'inactive' : query.facets.status?.join(','),
          /*
            FILES ONLY on the scopes that are about documents rather than about the tree — All
            files says so in its own name, and Expired is a retention question, which only a
            controlled document carries. It overrules the Type facet rather than sitting beside
            it: the rail is the control the reader just used, which is the same call Trash makes
            over Status.
          */
          type: FILES_ONLY_SCOPES.has(scope) ? 'file' : query.facets.type?.join(','),
          allFolders: scope === 'all',
          pinnedOnly: scope === 'pinned',
          expiredOnly: scope === 'expired',
          mineOnly: scope === 'mine',
          sharedOnly: scope === 'shared',
        },
        signal,
      );

      setContext(result.context);
      setSiblingNames(result.page.items.map((item) => item.name));

      return result.page;
    },
    [folderId, scope],
  );

  /*
    The breadcrumb IS the back-nav, and on this module it is also what states a row's
    position. Every segment above the folder on screen links; the last one never does.
  */
  const breadcrumb = useMemo<BreadcrumbSegment[]>(() => {
    const segments: BreadcrumbSegment[] = [
      'Document Management',
      { label: 'Repository', to: '/repository' },
    ];

    for (const crumb of context?.breadcrumbs ?? []) {
      segments.push({ label: crumb.name, to: `/repository?folder=${crumb.id}` });
    }

    return segments;
  }, [context]);

  /**
   * A folder owned by another module is filed into by that module through code, never by a
   * reader browsing the tree — so Create is withheld here. Disabled for STATE with the reason,
   * not hidden: the reader is not short a permission, and a vanished button would read as one
   * they lack.
   */
  const ownerModuleKey = context?.ownerModuleKey ?? null;

  /**
   * Fetches the file through the authenticated client and hands it to the browser.
   *
   * **An object URL, not an anchor pointed at the endpoint.** A browser navigation carries no
   * bearer token, so a bare href would arrive unauthenticated — the same trap the personnel
   * photograph documents, and one that fails silently rather than loudly.
   */
  const download = useCallback(async (item: RepositoryItemListItem) => {
    setError(null);

    try {
      const { blob, fileName } = await repositoryApi.download(item.id);

      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');

      anchor.href = url;
      anchor.download = fileName ?? item.name;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not download the file.', 0),
      );
    }
  }, []);

  /**
   * Pins or unpins one row, then reloads the listing.
   *
   * The reload is what keeps the entry's own label honest — `isPinned` rides on the row, so a
   * menu opened again after a pin has to be reading a row the server has re-answered. It is
   * also what makes the pinned view empty itself as the reader unpins from inside it.
   */
  const togglePin = useCallback(
    async (item: RepositoryItemListItem) => {
      setError(null);

      try {
        await repositoryApi.togglePin(item.id);
        refresh();
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not update the pin.', 0),
        );
      }
    },
    [refresh],
  );

  const rowActions = useCallback(
    (item: RepositoryItemListItem): MenuItem[] => {
      const isFolder = item.itemType === RepositoryItemType.Folder;

      return [
        {
          key: `open-${item.id}`,
          label: 'Open',
          icon: <FolderOpen className="h-4 w-4" aria-hidden="true" />,
          permission: 'repository.read',
          disabledReason: isFolder ? null : 'Only a folder can be opened.',
          onClick: () => openFolder(item.id),
        },
        {
          key: `edit-`,
          label: 'Edit',
          permission: 'repository.update',
          /*
            TWO reasons it can be disabled, and they are different facts about the row.

            The owner-module one is about WHERE it is filed; the access one is about what this
            reader was given. Both are STATE rather than a gate — the key above already hides
            the entry from somebody who cannot update anything — and disabled-with-a-reason is
            what the product does with state.

            `accessLevel` is resolved per caller on every read, so this is the same answer the
            server would give rather than a guess the client makes.
          */
          disabledReason: item.ownerModuleKey
            ? `This folder belongs to the  module and is maintained by it.`
            : !hasPermission(item.permissions, RepositoryPermissions.Edit)
              ? "You can view this but not change it. Ask its owner for edit access."
              : null,
          onClick: () => setEditing(item),
        },
        {
          /*
            SHARE reaches an item from the LISTING, so a folder can be opened up without being
            navigated into — the Share chip on the document profile is the same dialog for the
            case where the reader is already inside.

            No `disabledReason`: the key is the door and the dialog resolves the room, opening
            read-only for somebody who may see the grants and not change them. Refusing here
            would have to re-derive server state the row does not carry.
          */
          key: `share-`,
          label: 'Share',
          icon: <Share2 className="h-4 w-4" aria-hidden="true" />,
          permission: 'repository.share',
          onClick: () => setSharing(item),
        },
        {
          key: `restore-${item.id}`,
          label: 'Restore',
          icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
          permission: 'repository.restore',
          disabledReason: item.isActive ? 'This item is already active.' : null,
          onClick: () => setConfirming({ kind: 'activate', item }),
        },
      ];
    },
    [openFolder],
  );

  /**
   * Pin and Download, one click away on every row.
   *
   * **Captured from GRC's Documents workspace**, whose rows carry a star and a download beside
   * the kebab — a repository is used like a file browser, and these are what a reader does to
   * most rows in one.
   *
   * **PROMOTED OUT of `rowActions`, not duplicated into it.** They were entries in the kebab
   * and are the same `MenuItem`s moved, so each keeps its own permission and its own disabled
   * reason and neither can be restated differently in two places.
   *
   * GRC's third inline action is SHARE, and it is absent rather than disabled: sharing needs a
   * visibility model this product has not decided on, and a chip that opens nothing is the dead
   * affordance the hide-rule forbids.
   */
  const rowQuickActions = useCallback(
    (item: RepositoryItemListItem): MenuItem[] => {
      const isFolder = item.itemType === RepositoryItemType.Folder;

      return [
        {
          key: 'pin',
          label: item.isPinned ? 'Unpin' : 'Pin',
          // The STAR the profile toolbar already uses for this, not a pin glyph — one action
          // offered on three surfaces must not be drawn three ways. Filled while pinned, which
          // is the same state treatment the toolbar chip takes.
          icon: item.isPinned ? (
            <Star className="h-4 w-4 fill-current text-brand-500" aria-hidden="true" />
          ) : (
            <Star className="h-4 w-4" aria-hidden="true" />
          ),
          /*
            `repository.read`, not a write key: a pin is a bookmark on this reader's own view
            and changes nothing anyone else sees. Gating a personal preference behind a write
            grant would stop a read-only reader bookmarking anything.
          */
          permission: 'repository.read',
          // A deleted item is out of the tree, so a shortcut to it would be a dead link. The
          // server refuses it too; the reason is here so the reader is not refused silently.
          disabledReason: item.isActive ? null : 'Restore this item before pinning it.',
          onClick: () => void togglePin(item),
        },
        {
          key: 'download',
          label: 'Download',
          icon: <Download className="h-4 w-4" aria-hidden="true" />,
          permission: 'repository.download',
          /*
            The null check stays: rows written before 17 Sep 2026 can still be reservations —
            a number allocated with no file behind it — and the server refuses their download
            rather than streaming nothing.
          */
          disabledReason: isFolder
            ? 'A folder has no file to download.'
            : item.storedFileId === null
              ? 'This document has no file.'
              : null,
          onClick: () => void download(item),
        },
      ];
    },
    [download, togglePin],
  );

  /**
   * The two things that can be made here, built ONCE and rendered twice.
   *
   * The toolbar's Create menu and the canvas's right-click menu are the same two acts, so they
   * read one array: two lists would be two chances for a permission, a disabled reason or a
   * label to drift, and the right-click is a SHORTCUT for the toolbar rather than a second
   * place to look. That equivalence is also what makes the background menu acceptable at all —
   * it cannot be reached by keyboard, so nothing may live only in it.
   */
  const createMenu = useMemo<MenuItem[]>(
    () => [
      {
        key: 'new-folder',
        label: 'New folder',
        icon: <FolderPlus className="h-4 w-4" aria-hidden="true" />,
        permission: 'repository.create',
        onClick: () => setCreating(true),
      },
      {
        key: 'upload-files',
        label: 'Upload files',
        icon: <Upload className="h-4 w-4" aria-hidden="true" />,
        permission: 'repository.create',
        /*
          Disabled for STATE, with the reason — the reader acts on it by opening a folder.
          Hiding it would read as a permission they lack, which is a different thing and not
          true.

          The ROOT is the only place uploading is refused, and it is refused because the root
          holds the seeded containers rather than documents. Every other folder takes an
          upload, coded or not: the folder decides what the file BECOMES, not whether it is
          allowed.
        */
        disabledReason: folderId
          ? null
          : 'Open a folder first — a file is uploaded into a folder rather than to the root.',
        onClick: () => fileInputRef.current?.click(),
      },
    ],
    [folderId],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await repositoryApi.deactivate(confirming.item.id);
      else await repositoryApi.activate(confirming.item.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  const canUploadHere = Boolean(folderId) && !ownerModuleKey;

  return (
    <>
      {/*
        The CANVAS — a file-explorer surface over the whole listing, carrying two gestures the
        toolbar cannot: right-click to create, and drop files to upload. Captured from GRC's
        Documents workspace, which is where both belong in a product people use like a file
        browser.

        `h-full min-h-0` and nothing else: `<IndexPage>` owns the layout underneath, and a
        wrapper that did not pass its height through would collapse the page it frames.
      */}
      <div
        data-repository-canvas=""
        className={`flex h-full min-h-0 min-w-0 flex-col ${
          dragging ? 'ring-2 ring-inset ring-blue-400' : ''
        }`}
        onDragOver={(event) => {
          // Only a drag carrying FILES, so dragging a text selection across the page does not
          // light up a drop zone that would reject it.
          if (!canUploadHere || !event.dataTransfer.types.includes('Files')) return;

          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          // Fires on every child boundary too, so only the one leaving the canvas itself counts
          // — otherwise the ring flickers off as the pointer crosses a row.
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;

          setDragging(false);
        }}
        onDrop={(event) => {
          if (!canUploadHere) return;

          event.preventDefault();
          setDragging(false);

          const dropped = Array.from(event.dataTransfer.files ?? []);

          if (dropped.length > 0) queueFiles(dropped);
        }}
      >
        {/*
          The background context menu. No trigger — it is opened only by right-clicking the
          canvas, and it is a SHORTCUT: every entry is the toolbar's own Create menu, so
          nothing lives only here and a keyboard reader loses nothing.

          A right-click on a ROW never reaches this. `<RowActionMenu>` stops propagation on the
          row before deciding what to do with the gesture, which is what keeps the two menus
          from both opening on one click.
        */}
        <Menu
          id="repository-canvas-menu"
          contextMenuFor="[data-repository-canvas]"
          items={createMenu}
        />

      <IndexPage<RepositoryItemListItem>
        module="repository"
        breadcrumb={breadcrumb}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'type', label: 'Type' },
        ]}
        /*
          EVERY entity this module writes, not just the parent.

          `<HistoryLogsModal>` filters on `entityType`, so a list naming only `RepositoryItem`
          hides every write to every child — and the failure is silent in the worst way: the row
          IS in AuditLogs and the general Audit Log page shows it, so only the module's own
          history is blind.

          `RepositoryShare` is the one that matters most here. A grant table flips `IsActive`
          rather than deleting precisely so the trail of who widened access to what survives —
          and leaving it off this list would hide that trail on the one screen a reader of this
          module would look for it.
        */
        /*
          NO FOLDER-TAB STRIP. Product call, 20 Sep 2026.

          A repository is browsed by FOLDER, and the breadcrumb is what says where the reader
          is. A strip reading All / Active / Inactive above it was a second, competing answer
          to the same question — and one the reader had not asked, since the resting state
          already hides deleted rows.

          Show deleted is untouched: it lives in the settings cog and reads the same status
          facet, which is still declared. The strip is what goes, not the axis.
        */
        folderNavFacet={null}
        auditEntityTypes={[
          'RepositoryItem',
          'RepositoryShare',
          'RepositoryPin',
          'RepositoryDocumentVersion',
        ]}
        rowKey={(item) => item.id}
        fetch={fetchItems}
        reloadKey={reloadKey}
        /*
          The placeholder names the SCOPE the search will run over, because a view scope has
          already changed it — "Search this folder" over a pinned listing is a label stating
          something the list is not doing.
        */
        searchPlaceholder={SEARCH_PLACEHOLDER[scope]}
        /*
          The empty message names the SCOPE too, and each one says what would put something
          here. "This folder is empty" over the Expired listing would be stating something the
          list is not showing.
        */
        emptyMessage={
          EMPTY_MESSAGE[scope] ??
          (ownerModuleKey
            ? `Nothing filed here yet. The ${ownerModuleKey} module files into this folder.`
            : 'This folder is empty. Create a folder, or file something into it.')
        }
        /*
          Two things can be created here and they are not the same act: a folder, and a file
          uploaded into the folder on screen. A menu keeps one control in the standard place
          and says on the entry itself why uploading is unavailable, rather than disabling the
          whole button for a reason that only applies to half of it.

          Uploading is offered in EVERY folder the reader may write to, coded or not — the
          folder decides what the file becomes (a controlled document with a reference number,
          or a plain file) rather than whether it is allowed at all.
        */
        createMenu={createMenu}
        /*
          The view scopes. GRC puts these in a left rail inside the module; here they are
          entries in the cog beside Show deleted, because each changes what the list IS rather
          than which slice of it is showing — and one list with one toolbar beats four pages.
        */
        /*
          The DEDICATED rail, replacing the standard facet rail — product call, 18 Sep 2026.

          A repository's left column is navigation: where am I, and which listing am I looking
          at. That is not what `<IndexFilterRail>` is for, and bending it to also draw folders
          would put this module's navigation into the component every other module depends on.
          `<IndexPage>`'s `rail` slot is what makes that a replacement rather than a fork.
        */
        rail={
          <RepositoryNavRail
            scope={scope}
            folderId={folderId}
            onScopeChange={(next) => {
              setScope(next);

              /*
                A tree-wide scope has no folder to be standing in, so the breadcrumb goes back
                to the root rather than naming a folder the listing is not showing.
              */
              if (next !== 'folder') setSearchParams({});
            }}
            onOpenFolder={openFolder}
            pinned={pinnedItems}
            reloadKey={reloadKey}
          />
        }
        /*
          The view scopes MOVED to the rail — All files, Pinned, Expired and Trash are rows
          there now, where a file browser's reader looks for them. They are deliberately not
          also in the cog: one control per scope, and a second place to turn Pinned on is a
          second place for the two to disagree about what is showing.
        */
        createPermission="repository.create"
        createDisabledReason={
          ownerModuleKey
            ? `This folder belongs to the ${ownerModuleKey} module and is filed into by it.`
            : null
        }
        /*
          Only a FILE gets a profile — product call, 15 Sep 2026, and in the deviations table.

          A folder is not a record, it is a PLACE: "what is this one" is answered by its
          contents, which is what clicking it shows. A profile behind a folder would put a page
          of provenance between the reader and the thing they asked for, on the one gesture a
          file browser has. A folder's provenance is in its Other Info popover, where audit
          metadata lives on every surface that is not a profile.

          A file's profile is built — it is where the reference number, the classification, the
          unit that uploaded it and its trail live.
        */
        onRowOpen={(item) =>
          item.itemType === RepositoryItemType.Folder
            ? openFolder(item.id)
            : navigate(`/repository/documents/${item.id}`)
        }
        openPermission="repository.read"
        rowActions={rowActions}
        rowQuickActions={rowQuickActions}
        // The one page drawing them as glyphs — an approved exception, 13 Oct 2026.
        rowQuickActionStyle="icon"
        // No Latest update, in either view — product call, 13 Oct 2026.
        latestUpdate={false}
        onDelete={(item) => setConfirming({ kind: 'deactivate', item })}
        deletePermission="repository.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(item) => {
          if (!item.isActive) return 'This item is already deleted.';
          if (item.ownerModuleKey) {
            return `This folder belongs to the ${item.ownerModuleKey} module and is maintained by it.`;
          }
          if (item.childCount > 0) {
            return `This folder still holds ${item.childCount} active item${
              item.childCount === 1 ? '' : 's'
            }. Move or delete them first.`;
          }
          return null;
        }}
        onClearData={() => repositoryApi.clear()}
        clearDataPermission="repository.clear-data"
        otherInfo={(item) => item}
      />
      </div>

      {sharing !== null ? (
        /*
          Refreshes on save because a share can change what THIS reader sees — opening a folder
          to the organization, or closing it, moves rows in and out of the listing behind the
          dialog.
        */
        <ShareModal
          open
          itemId={sharing.id}
          onClose={() => setSharing(null)}
          onSaved={refresh}
        />
      ) : null}

      {creating || editing !== null ? (
        <FolderModal
          open
          editing={editing}
          parentId={folderId}
          parentName={context?.folder?.name ?? null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {/*
        The hidden input the Upload entry clicks. A file picker cannot be opened from code
        any other way, and the queue is where a chosen file goes — so there is no modal here,
        only the control that reaches the OS dialog.
      */}
      <input
        ref={fileInputRef}
        id="repository-upload-input"
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={(event) => {
          queueFiles(event.target.files);
          // Cleared so choosing the same file again still fires a change event.
          event.target.value = '';
        }}
      />

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete item' : 'Restore item'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring puts something back.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`repository-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.item.name}" is hidden from the repository.`
                  : `"${confirming.item.name}" is returned to the repository.`}
              </p>

              <p>
                Nothing is erased — a deleted item can be restored from this list, and any
                stored file behind it is kept so the restore is faithful.
              </p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * The mark that says whether a row is a folder or a file — and, for a folder, whether it is
 * PRIVATE.
 *
 * A private folder draws a closed one. That is not decoration: visibility gates READING, so a
 * folder with no mark on it would hide its contents from every other reader with nothing on
 * screen saying why — and the reader who made it could not tell which of their folders was
 * which. The glyph is the whole affordance rather than a column, because it costs no width and
 * the slot identifying the row is where a fact about the row's identity belongs.
 *
 * It is `aria-hidden` like every other row glyph, so the state travels as an `sr-only` twin
 * beside it — the pairing `<HelpTip>` and the calendar's read-only cells already use. A
 * tooltip would be the wrong control here: a full table draws a couple of hundred cells, and a
 * portal-rendering component in each is a cost paid for nothing.
 *
 * It identifies the row, so it is the list view's **avatar** — spanning both lines and
 * centring against the select and `#` gutters — and the table view's leading glyph. The
 * anchor column supplies it in both places, and this drops its own copy on the list surface
 * so the row does not draw two: the list hoists the `avatar` out and renders it itself, while
 * `render` still runs for the title.
 *
 * The same pairing `<AvatarCell>` uses, for the same reason. See `listSurface.ts`.
 */
function ItemIcon({
  item,
  asAvatar = false,
}: {
  item: RepositoryItemListItem;
  /**
   * True in the `avatar` slot, which the list renders itself.
   *
   * The slot sits INSIDE `ListSurfaceContext` — the provider wraps the whole list, not just
   * the cells — so without this flag the hoisted copy would suppress itself along with the
   * inline one and the list would show no icon at all. The flag says "you are the hoisted
   * one", not "you are outside the context".
   */
  asAvatar?: boolean;
}) {
  const inList = useListSurface();

  // The inline copy stands down on the list surface so the row does not draw two.
  if (inList && !asAvatar) return null;

  const className = asAvatar
    ? 'h-5 w-5 shrink-0 text-slate-400'
    : 'h-4 w-4 shrink-0 text-slate-400';

  if (item.itemType !== RepositoryItemType.Folder) {
    return <FileText className={className} aria-hidden="true" />;
  }

  if (item.publicPermissions !== RepositoryPermissions.None) {
    return <Folder className={className} aria-hidden="true" />;
  }

  return (
    <>
      <FolderLock className={className} aria-hidden="true" />
      <span className="sr-only">Private folder. </span>
    </>
  );
}

const COLUMNS: ColumnDef<RepositoryItemListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 340,
    flex: true,
    sortable: true,
    /*
      The icon identifies the row, so it is the list's AVATAR rather than a glyph inside the
      cell — it has to span both lines and centre against the select and `#` gutters, and a
      leading icon written into `render` would sit on the title line with the provenance line
      hanging under it. The table view draws it from here; the list hoists it out.
    */
    avatar: (item) => <ItemIcon item={item} asAvatar />,
    render: (item) => (
      <span className="flex items-center gap-2">
        <ItemIcon item={item} />
        <TextCell value={item.name} />
      </span>
    ),
  },
  {
    key: 'referenceNumber',
    header: 'Reference',
    width: 190,
    summary: true,
    /*
      The permanent identifier a controlled document is quoted by. Mono, like every machine
      value — and not sortable, because the server's allow-list does not carry it: sorting on
      a composed string would order L2-SAL-26-009 after L2-SAL-26-010 anyway, which reads as
      broken rather than as text order.

      An em dash is the honest value for a plain file: it sits outside a coded branch, so there
      is nothing to build a number from. That is not an absence somebody has to fix.
    */
    render: (item) => <TextCell value={item.referenceNumber} mono />,
  },
  {
    key: 'classificationName',
    header: 'Classification',
    width: 170,
    summary: true,
    // A LIVE view resolved on every read, never a copy — correcting the vocabulary corrects
    // every document naming it.
    render: (item) => <TextCell value={item.classificationName} />,
  },
  {
    key: 'folderCode',
    header: 'Code',
    width: 110,
    sortable: true,
    // Mono: a folder code is a machine value, and it is a segment of every reference number
    // issued beneath it.
    render: (item) => <TextCell value={item.folderCode} mono />,
  },
  {
    key: 'childCount',
    header: 'Items',
    width: 90,
    align: 'right',
    summary: true,
    render: (item) =>
      item.itemType === RepositoryItemType.Folder ? (
        <TextCell value={String(item.childCount)} mono numeric />
      ) : (
        <TextCell value={null} />
      ),
  },
  {
    key: 'description',
    header: 'Description',
    width: 300,
    summary: true,
    render: (item) => <TextCell value={item.description} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    /*
      DELETED only — product call, 13 Oct 2026: "Active" is not a state a repository reader
      tracks, since the resting list already hides deleted items. The badge survives for the
      one case it tells them something: a deleted row mixed in by Show deleted.
    */
    render: (item) => (item.isActive ? null : <StatusText tone="inactive">Deleted</StatusText>),
  },
];

/**
 * What the search box says it will search, per scope.
 *
 * A view scope has already changed what the list IS, so "Search this folder" over a pinned or
 * expired listing is a label stating something the list is not doing.
 */
const SEARCH_PLACEHOLDER: Record<RepositoryScope, string> = {
  folder: 'Search this folder',
  mine: 'Search your documents',
  shared: 'Search what is shared with you',
  all: 'Search every folder',
  pinned: 'Search your pinned items',
  expired: 'Search expired documents',
  trash: 'Search the trash',
};

/**
 * What an empty listing says, per scope.
 *
 * Each names the scope and says what would put something in it — "This folder is empty" over
 * the Expired listing would be stating something the list is not showing.
 *
 * **`folder` is absent deliberately.** Browsing a folder has two empty messages rather than
 * one — an owned folder is filed into by its module, an ordinary one by the reader — so the
 * page resolves that case itself and this map answers for the scopes that have a single reply.
 */
const EMPTY_MESSAGE: Partial<Record<RepositoryScope, string>> = {
  mine: 'You have not created anything yet. What you upload or create appears here.',
  shared:
    'Nothing has been shared with you. A folder or document somebody shares appears here.',
  all: 'No files yet. Upload one into a folder and it appears here.',
  pinned: 'Nothing pinned. Pin a folder or a document from its row actions to keep it here.',
  expired:
    'Nothing has expired. A controlled document expires when its retention window runs out.',
  trash: 'The trash is empty. A deleted item is kept here and can be restored.',
};
